/** Step 2 — 活动选择 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSettings } from '../../components/providers/AppSettingsProvider';
import { loadAuthUser } from '../../services/auth';
import { createPageUrl } from '../../utils';
import { CheckInShell } from './components/CheckInShell';
import { CHECK_IN_EVENT_OPTIONS } from './data/events';
import { CUSTOM_ACTIVITY_ICON_CHOICES } from './data/activityIcons';
import { useCustomEvents } from './hooks/useCustomEvents';

const glassTileClass =
  'flex aspect-square w-[5.25rem] shrink-0 flex-col items-center justify-center gap-2 rounded-2xl border border-white/35 bg-white/[0.12] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] backdrop-blur-xl backdrop-saturate-150 transition-transform active:scale-[0.98] sm:w-[5.75rem]';

const customTileClass =
  'flex aspect-square w-[5.25rem] shrink-0 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-white/45 bg-transparent text-white transition-transform active:scale-[0.98] sm:w-[5.75rem]';

function periodKeyForHour(hour: number): string {
  if (hour < 12) return 'checkIn.eventPeriodMorning';
  if (hour < 18) return 'checkIn.eventPeriodAfternoon';
  return 'checkIn.eventPeriodEvening';
}

type SelectedEvent = { tag: string; emoji?: string };

const CheckInEvent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useAppSettings();
  const moodTag = (location.state as { moodTag?: string } | null)?.moodTag;
  const [selected, setSelected] = useState<SelectedEvent | null>(null);
  const savedCustomEvents = useCustomEvents();

  const periodKey = useMemo(() => periodKeyForHour(new Date().getHours()), []);
  const moodLabel = useCallback(
    () => (moodTag ? t(`checkIn.tags.${moodTag}`) || moodTag : ''),
    [moodTag, t],
  );
  const eventLabel = useCallback((id: string) => t(`checkIn.events.${id}`) || id, [t]);

  useEffect(() => {
    if (!loadAuthUser()) {
      navigate('/splash', { replace: true, state: { forceLogin: true } });
    }
  }, [navigate]);

  useEffect(() => {
    if (!moodTag) navigate('/check-in', { replace: true });
  }, [moodTag, navigate]);

  if (!moodTag) return null;

  const presetOptions = CHECK_IN_EVENT_OPTIONS.filter((o) => !o.isCustom);
  const customOpt = CHECK_IN_EVENT_OPTIONS.find((o) => o.isCustom)!;

  const handleContinue = () => {
    if (!moodTag || !selected) return;
    navigate(createPageUrl('CheckInSummary'), {
      state: { moodTag, activityTag: selected.tag, activityEmoji: selected.emoji },
    });
  };

  return (
    <CheckInShell
      ctaLabel={t('checkIn.continue')}
      onCta={handleContinue}
      ctaDisabled={!selected}
    >
      <div className="mt-5 text-center">
        <p className="text-lg font-medium tracking-tight text-white">
          {t('checkIn.eventThanks')}
        </p>
        <p className="mt-3 text-base font-normal leading-snug text-white/90">
          {t('checkIn.eventQuestionPrefix', { period: t(periodKey) })}
          <em className={`font-medium text-white ${language === 'zh-CN' ? 'not-italic' : 'italic'}`}>
            {moodLabel()}
          </em>
          {t('checkIn.eventQuestionSuffix')}
        </p>
      </div>

      <div className="mt-8 w-full max-w-full">
        <p className="mb-3 text-center text-[11px] font-medium text-white/45">
          {t('checkIn.eventSwipeHint')}
        </p>
        <div
          className="-mx-4 -my-2 overflow-x-auto overscroll-x-contain px-4 py-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          aria-label={t('checkIn.eventPicker')}
        >
          <div className="mx-auto grid grid-rows-3 grid-flow-col gap-x-3 gap-y-3 sm:gap-x-3.5" role="list">
            {/* 预设事件 */}
            {presetOptions.map(({ id, icon: Icon }) => {
              const on = selected?.tag === id && !selected.emoji;
              return (
                <button
                  key={id}
                  type="button"
                  role="listitem"
                  onClick={() => setSelected({ tag: id })}
                  className={`${glassTileClass} ${
                    on
                      ? 'scale-[1.03] ring-2 ring-white/90 ring-offset-2 ring-offset-[#0a1224]'
                      : 'opacity-[0.97] hover:opacity-100'
                  }`}
                >
                  <Icon className="h-6 w-6 shrink-0 opacity-95" strokeWidth={1.75} aria-hidden />
                  <span className="px-1 text-center text-[11px] font-medium leading-tight">
                    {eventLabel(id)}
                  </span>
                </button>
              );
            })}

            {/* 已保存的自定义事件（紧靠 "+" 左侧） */}
            {savedCustomEvents.map((evt) => {
              const iconChoice = CUSTOM_ACTIVITY_ICON_CHOICES.find((c) => c.id === evt.iconId);
              const Icon = iconChoice?.Icon;
              const on = selected?.tag === evt.title && selected.emoji === evt.emoji;
              return (
                <button
                  key={`custom-${evt.id}`}
                  type="button"
                  role="listitem"
                  onClick={() => setSelected({ tag: evt.title, emoji: evt.emoji })}
                  className={`${glassTileClass} ${
                    on
                      ? 'scale-[1.03] ring-2 ring-white/90 ring-offset-2 ring-offset-[#0a1224]'
                      : 'opacity-[0.97] hover:opacity-100'
                  }`}
                >
                  {Icon ? (
                    <Icon className="h-6 w-6 shrink-0 opacity-95" strokeWidth={1.75} aria-hidden />
                  ) : (
                    <span className="text-xl leading-none" aria-hidden>{evt.emoji}</span>
                  )}
                  <span className="px-1 text-center text-[11px] font-medium leading-tight">
                    {evt.title}
                  </span>
                </button>
              );
            })}

            {/* "+" 新建自定义事件，始终在最后一格 */}
            <button
              key={customOpt.id}
              type="button"
              role="listitem"
              onClick={() =>
                navigate(createPageUrl('CheckInEventCustomize'), { state: { moodTag } })
              }
              className={`${customTileClass} opacity-[0.97] hover:opacity-100`}
            >
              <customOpt.icon className="h-6 w-6 shrink-0 opacity-95" strokeWidth={1.75} aria-hidden />
              <span className="px-1 text-center text-[11px] font-medium leading-tight">
                {eventLabel(customOpt.id)}
              </span>
            </button>
          </div>
        </div>
      </div>
    </CheckInShell>
  );
};

export default CheckInEvent;
